// Completely Dynamic Currency formatting utility - fetches ALL from database
import { supabase } from '../../config/supabase';

// Minimal fallback - only used if database is completely unavailable
const MINIMAL_FALLBACK = {
  PKR: { symbol: '₨', code: 'PKR', locale: 'en-PK', decimals: 0 }
};

const DEFAULT_CURRENCY = 'PKR';

// Get currency by location from database
export const getCurrencyByLocation = async (country = '', city = '') => {
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('currency')
      .ilike('name', `%${country}%`)
      .limit(1)
      .single();

    if (error || !data) {
      return DEFAULT_CURRENCY;
    }

    return data.currency || DEFAULT_CURRENCY;
  } catch (error) {
    console.error('Error fetching currency by location:', error);
    return DEFAULT_CURRENCY;
  }
};

// Get user's preferred currency from localStorage or default to PKR
export const getUserCurrency = () => {
  try {
    return localStorage.getItem('userCurrency') || DEFAULT_CURRENCY;
  } catch (error) {
    console.error('Error getting user currency:', error);
    return DEFAULT_CURRENCY;
  }
};

// Set user's preferred currency
export const setUserCurrency = (currency) => {
  try {
    localStorage.setItem('userCurrency', currency);
  } catch (error) {
    console.error('Error setting user currency:', error);
  }
};

// Get currency configuration from database
export const getCurrencyConfig = async (currency = null) => {
  try {
    const targetCurrency = currency || getUserCurrency();
    
    const { data, error } = await supabase
      .from('countries')
      .select('currency, currency_symbol')
      .eq('currency', targetCurrency)
      .limit(1)
      .single();

    if (error || !data) {
      return MINIMAL_FALLBACK.PKR;
    }

    return {
      symbol: data.currency_symbol,
      code: data.currency,
      locale: getLocaleForCurrency(data.currency),
      decimals: getDecimalsForCurrency(data.currency)
    };
  } catch (error) {
    console.error('Error getting currency config:', error);
    return MINIMAL_FALLBACK.PKR;
  }
};

// Format amount with currency code in English (e.g., PKR, KWD, AED) and proper formatting (async version)
export const formatCurrency = async (amount, currency = null) => {
  try {
    if (amount === null || amount === undefined || isNaN(amount)) {
      const config = await getCurrencyConfig(currency);
      return `${config.code} 0`;
    }

    const config = await getCurrencyConfig(currency);
    const numericAmount = Number(amount);
    const currencyCode = config.code || currency || 'PKR'; // Use currency code in English

    // For currencies with 0 decimals, round to whole number
    if (config.decimals === 0) {
      return `${currencyCode} ${Math.round(numericAmount).toLocaleString()}`;
    }

    // For currencies with decimals, format with proper decimal places
    return `${currencyCode} ${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals
    })}`;
  } catch (error) {
    console.error('Error formatting currency:', error);
    const config = await getCurrencyConfig(currency);
    const currencyCode = config.code || currency || 'PKR';
    return `${currencyCode} ${Number(amount).toFixed(config.decimals)}`;
  }
};

// Convert currency using exchange rates from database
export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  try {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    // Try to get exchange rate from database
    const { data, error } = await supabase
      .from('currency_rates')
      .select('rate')
      .eq('from_currency', fromCurrency)
      .eq('to_currency', toCurrency)
      .single();

    if (error || !data) {
      // Try reverse rate
      const { data: reverseData, error: reverseError } = await supabase
        .from('currency_rates')
        .select('rate')
        .eq('from_currency', toCurrency)
        .eq('to_currency', fromCurrency)
        .single();

      if (reverseError || !reverseData) {
        console.warn(`No exchange rate found for ${fromCurrency} to ${toCurrency}`);
        return amount; // Return original amount if no rate found
      }

      // Use reverse rate
      return Number(amount) / Number(reverseData.rate);
    }

    return Number(amount) * Number(data.rate);
  } catch (error) {
    console.error('Error converting currency:', error);
    return amount;
  }
};

// Format amount in user's currency with conversion if needed
export const formatCurrencyForUser = async (amount, vendorCurrency, userCurrency = null) => {
  try {
    const targetCurrency = userCurrency || getUserCurrency();
    
    if (vendorCurrency === targetCurrency) {
      return formatCurrency(amount, targetCurrency);
    }

    // Convert to user's currency
    const convertedAmount = await convertCurrency(amount, vendorCurrency, targetCurrency);
    return formatCurrency(convertedAmount, targetCurrency);
  } catch (error) {
    console.error('Error formatting currency for user:', error);
    return formatCurrency(amount, vendorCurrency);
  }
};

// Get all available currencies from database
export const getAvailableCurrencies = async () => {
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('currency, currency_symbol, name')
      .not('currency', 'is', null)
      .not('currency', 'eq', '')
      .order('currency');

    if (error || !data) {
      return [{
        code: 'PKR',
        symbol: '₨',
        locale: 'en-PK',
        decimals: 0,
        country: 'Pakistan'
      }];
    }

    return data.map(country => ({
      code: country.currency,
      symbol: country.currency_symbol,
      locale: getLocaleForCurrency(country.currency),
      decimals: getDecimalsForCurrency(country.currency),
      country: country.name
    }));
  } catch (error) {
    console.error('Error getting available currencies:', error);
    return [{
      code: 'PKR',
      symbol: '₨',
      locale: 'en-PK',
      decimals: 0,
      country: 'Pakistan'
    }];
  }
};

// Legacy function for backward compatibility
export const formatPKR = (amount) => {
  return formatCurrency(amount, 'PKR');
};

// Helper functions
const getLocaleForCurrency = (currency) => {
  const localeMap = {
    'PKR': 'en-PK',
    'USD': 'en-US',
    'AED': 'ar-AE',
    'SAR': 'ar-SA',
    'KWD': 'en-KW',
    'GBP': 'en-GB',
    'EUR': 'en-EU',
    'INR': 'en-IN',
    'BDT': 'bn-BD',
    'GHS': 'en-GH',
    'NGN': 'en-NG',
    'ZAR': 'en-ZA',
    'KES': 'en-KE',
    'EGP': 'ar-EG',
    'MAD': 'ar-MA'
  };
  return localeMap[currency] || 'en-US';
};

const getDecimalsForCurrency = (currency) => {
  const decimalMap = {
    'PKR': 0,
    'INR': 0,
    'BDT': 0,
    'JPY': 0,
    'KWD': 3,
    'BHD': 3,
    'OMR': 3,
    'JOD': 3,
    'USD': 2,
    'AED': 2,
    'SAR': 2,
    'GBP': 2,
    'EUR': 2,
    'GHS': 2,
    'NGN': 2,
    'ZAR': 2,
    'KES': 2,
    'EGP': 2,
    'MAD': 2
  };
  return decimalMap[currency] || 2;
};

// Sync version for backward compatibility (uses database with fallback) - shows currency code in English
export const formatCurrencySync = (amount, currency = null) => {
  try {
    if (amount === null || amount === undefined || isNaN(amount)) {
      const currencyCode = currency || 'PKR';
      return `${currencyCode} 0`;
    }

    const numericAmount = Number(amount);
    const currencyCode = currency || 'PKR'; // Use currency code in English
    const decimals = getDecimalsForCurrency(currencyCode);
    
    if (decimals === 0) {
      return `${currencyCode} ${Math.round(numericAmount).toLocaleString()}`;
    }
    
    return `${currencyCode} ${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}`;
  } catch (error) {
    console.error('Error formatting currency sync:', error);
    const currencyCode = currency || 'PKR';
    return `${currencyCode} ${Number(amount) || 0}`;
  }
};

// Sync version of currency symbol lookup (fallback)
const getCurrencySymbolSync = (currency) => {
  // This is a temporary fallback until we can make all components async
  const symbolMap = {
    'PKR': '₨',
    'USD': '$',
    'AED': 'د.إ',
    'SAR': 'ر.س',
    'KWD': 'KD',
    'GBP': '£',
    'EUR': '€',
    'INR': '₹',
    'BDT': '৳',
    'CAD': 'C$',
    'AUD': 'A$',
    'JPY': '¥',
    'CNY': '¥',
    'TRY': '₺',
    'MYR': 'RM',
    'THB': '฿',
    'SGD': 'S$',
    'KRW': '₩',
    'GHS': '₵',
    'NGN': '₦',
    'ZAR': 'R',
    'KES': 'KSh',
    'EGP': '£',
    'MAD': 'د.م.'
  };
  return symbolMap[currency] || '₨';
};

// Helper function to get currency symbol from database
const getCurrencySymbolFromDB = async (currency) => {
  try {
    if (!currency) return '₨';
    
    const { data, error } = await supabase
      .from('countries')
      .select('currency_symbol')
      .eq('currency', currency)
      .limit(1)
      .single();

    if (error || !data) {
      return '₨'; // Fallback to PKR
    }
    
    return data.currency_symbol || '₨';
  } catch (error) {
    console.error('Error fetching currency symbol:', error);
    return '₨'; // Fallback to PKR
  }
};

export default formatCurrency;